import { useMemo } from 'react'
import { FieldShell } from '../FieldShell'
import { useFieldContext, type FieldComponentProps } from '../rendererContext'
import type { EnumConfig } from '../types'
import { OptionsStatus } from './OptionsStatus'
import { optionByKey, optionKey, valuesEqual, visibleOptions, withTextLabels } from './optionUtils'
import { controlClass, useControlMeta } from './useControlMeta'
import { useResolvedOptions } from './useResolvedOptions'

export function SelectField({ field, path, value, errors }: FieldComponentProps) {
  const meta = useControlMeta(field, path, errors)
  const { enhancedSingleSelectAdapter: EnhancedSelect } = useFieldContext()
  const config = field.config as EnumConfig | undefined
  const { options: rawOptions, loading, error } = useResolvedOptions(field)
  const options = useMemo(() => withTextLabels(rawOptions), [rawOptions])
  const shown = visibleOptions(options, (v) => valuesEqual(v, value))

  if (field.display_only) {
    const selected = options.find((opt) => valuesEqual(opt.value, value))
    return (
      <FieldShell field={field} path={path} errors={errors}>
        <div id={meta.id} className="form-control-plaintext">
          {selected?.label ?? (value == null ? '' : String(value))}
        </div>
      </FieldShell>
    )
  }

  // A leading "none" choice unless explicitly hidden; lets the user clear the
  // selection. Its label is the placeholder or none_label when provided.
  const showNone = config?.hide_none !== true
  const noneLabel = config?.placeholder ?? config?.none_label ?? ''

  // Custom dropdown when the host wired an adapter; `plain_select` forces the
  // native <select> per field (mirrors MultiSelectField).
  if (EnhancedSelect != null && config?.plain_select !== true) {
    return (
      <FieldShell field={field} path={path} errors={errors}>
        <EnhancedSelect
          id={meta.id}
          options={shown}
          value={value}
          disabled={meta.disabled}
          noneLabel={showNone ? noneLabel : undefined}
          hasErrors={meta.hasErrors}
          ariaInvalid={meta.ariaInvalid}
          ariaRequired={meta.ariaRequired}
          ariaDescribedBy={meta.describedBy}
          classNames={meta.classNames}
          onChange={(next) => meta.setFieldValue(path, next)}
          onBlur={() => meta.notifyBlur(path)}
        />
        <OptionsStatus loading={loading} error={error} />
      </FieldShell>
    )
  }

  return (
    <FieldShell field={field} path={path} errors={errors}>
      <select
        id={meta.id}
        className={controlClass('form-select', meta.classNames.select, meta.hasErrors)}
        value={optionKey(value)}
        disabled={meta.disabled}
        aria-invalid={meta.ariaInvalid}
        aria-required={meta.ariaRequired}
        aria-describedby={meta.describedBy}
        onChange={(event) => {
          const opt = optionByKey(shown, event.target.value)
          meta.setFieldValue(path, opt ? opt.value : undefined)
        }}
        onBlur={() => meta.notifyBlur(path)}
      >
        {showNone ? <option value="">{noneLabel}</option> : null}
        {shown.map((opt) => (
          <option key={optionKey(opt.value)} value={optionKey(opt.value)}>
            {opt.label}
          </option>
        ))}
      </select>
      <OptionsStatus loading={loading} error={error} />
    </FieldShell>
  )
}
